import { useMemo, useState } from "react";
import styles from "./InboxScreen.module.css";

/** Curated native-Unicode emoji set, bundled locally: the panel's CSP
 *  (default-src 'self') blocks CDN-backed pickers, and native glyphs need
 *  no sprite sheets. Categories mirror WhatsApp's picker order. The sets are
 *  space-joined strings (split at module load) — one string per row keeps the
 *  file compact and formatter-stable. */
const SMILEYS =
  "😀 😃 😄 😁 😆 😅 🤣 😂 🙂 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😙 🥲 " +
  "😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🫡 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 " +
  "😮‍💨 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 " +
  "🥸 😎 🤓 🧐 😕 🫤 😟 🙁 😮 😯 😲 😳 🥺 🥹 😦 😧 😨 😰 😥 😢 " +
  "😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿 💀 💩 🤡 👻 👽";
const GESTURES =
  "👋 🤚 🖐️ ✋ 🖖 🫱 🫲 🫳 🫴 👌 🤌 🤏 ✌️ 🤞 🫰 🤟 🤘 🤙 👈 👉 " +
  "👆 👇 ☝️ 🫵 👍 👎 ✊ 👊 🤛 🤜 👏 🙌 🫶 👐 🤲 🤝 🙏 💪 🦾 ✍️ " +
  "💅 🤳 👀 👁️ 🧠 🗣️ 👤 👥 🧑 👩 👨 🧑‍💻 👮 🕵️ 💁 🙋 🙇 🤷 🤦 👶";
const HEARTS =
  "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❤️‍🔥 ❤️‍🩹 ❣️ 💕 💞 💓 💗 💖 💘 💝 " +
  "💟 💌 💋 💯 💢 💥 💫 💦 💨 🕳️ 💤 🌟 ✨ ⭐ 🔥 🎉 🎊 🎈 🎁 🏆";
const NATURE =
  "🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐸 🐵 🐔 🐧 🐦 🐤 🦆 " +
  "🦅 🦉 🐺 🐗 🐴 🦄 🐝 🦋 🐌 🐞 🐢 🐍 🐙 🦀 🐠 🐬 🐳 🌵 🌴 🌱 " +
  "🌿 🍀 🍁 🌸 🌼 🌻 🌹 🌈 ☀️ 🌙 ⛅ 🌧️ ⛈️ ❄️ 🌊 💧 🌍 🪐 🌋 🏔️";
const FOOD =
  "🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🫐 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🥑 🥦 " +
  "🌽 🥕 🧄 🧅 🥔 🍠 🥐 🍞 🥖 🧀 🥚 🍳 🧈 🥞 🧇 🥓 🍗 🍖 🌭 🍔 " +
  "🍟 🍕 🥪 🌮 🌯 🥗 🍜 🍝 🍛 🍣 🍤 🍙 🍚 🍦 🍰 🎂 🍫 🍬 ☕ 🧋";
const OBJECTS =
  "⚽ 🏀 🏈 ⚾ 🎾 🏐 🎱 🏓 🏸 🥊 🎯 🎮 🎲 ♟️ 🎨 🎤 🎧 🎸 🎹 🥁 " +
  "🚗 🚕 🚙 🚌 🏎️ 🚓 🚑 🚒 🛵 🚲 ✈️ 🚀 🛳️ ⛵ 🏠 🏢 🏪 🏥 ⏰ 📱 " +
  "💻 🖥️ 🖨️ 📷 📦 📮 ✏️ 📝 📌 📎 🔑 🔒 🔨 🧰 🛒 💰 💵 💳 🧾 📊";
const SYMBOLS =
  "✅ ☑️ ✔️ ❌ ❎ ➕ ➖ ➗ ✖️ 💲 ❓ ❗ ‼️ ⁉️ ⚠️ 🚫 ♻️ 💹 🔄 🔝 " +
  "🔜 🔙 🔛 🆗 🆕 🆓 🆒 🔞 📶 🔔 🔕 📢 📣 🔢 #️⃣ *️⃣ ▶️ ⏸️ 🔴 🟠 " +
  "🟡 🟢 🔵 🟣 ⚫ ⚪ 🟥 🟦 🟩 ⬛";

type Category = { key: string; label: string; icon: string; emojis: string[] };

const CATEGORIES: Category[] = [
  { key: "smileys", label: "Smileys", icon: "😀", emojis: SMILEYS.split(" ") },
  { key: "gestures", label: "Gestures & people", icon: "👍", emojis: GESTURES.split(" ") },
  { key: "hearts", label: "Hearts & feelings", icon: "❤️", emojis: HEARTS.split(" ") },
  { key: "nature", label: "Animals & nature", icon: "🌿", emojis: NATURE.split(" ") },
  { key: "food", label: "Food & drink", icon: "🍔", emojis: FOOD.split(" ") },
  { key: "objects", label: "Activity & objects", icon: "⚽", emojis: OBJECTS.split(" ") },
  { key: "symbols", label: "Symbols", icon: "✅", emojis: SYMBOLS.split(" ") },
];

const RECENTS_KEY = "wcp:emoji-recents";
const RECENTS_MAX = 16;

function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((e) => typeof e === "string") : [];
  } catch {
    return [];
  }
}

function saveRecents(list: string[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch {
    /* storage full/blocked — recents are best-effort */
  }
}

export function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const [recents, setRecents] = useState<string[]>(loadRecents);
  const tabs = useMemo<Category[]>(
    () =>
      recents.length > 0
        ? [{ key: "recent", label: "Recently used", icon: "🕘", emojis: recents }, ...CATEGORIES]
        : CATEGORIES,
    [recents],
  );
  const [tab, setTab] = useState("smileys");
  const active = tabs.find((t) => t.key === tab) ?? tabs[0];
  if (!active) return null;

  function pick(emoji: string) {
    const next = [emoji, ...recents.filter((e) => e !== emoji)].slice(0, RECENTS_MAX);
    setRecents(next);
    saveRecents(next);
    onPick(emoji);
  }

  return (
    <div className={styles.emojiMenu} role="menu" aria-label="Emoji picker">
      <div className={styles.emojiTabs}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={[styles.emojiTab, t.key === active.key ? styles.emojiTabOn : ""]
              .filter(Boolean)
              .join(" ")}
            title={t.label}
            aria-label={t.label}
            onClick={() => setTab(t.key)}
          >
            {t.icon}
          </button>
        ))}
      </div>
      <div className={styles.emojiGrid}>
        {active.emojis.map((e) => (
          <button
            key={e}
            type="button"
            className={styles.emojiCell}
            onClick={() => pick(e)}
            aria-label={`Insert ${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
